import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { fetchRepairs, fetchRepairStats, createRepair, updateRepairStatus, addRepairDevice } from '../redux/slices/repairsSlice';
import { fetchCustomers } from '../redux/slices/customersSlice';
import { fetchDealers } from '../redux/slices/dealersSlice';
import { fetchDevices } from '../redux/slices/devicesSlice';
import { Modal, LoadingSpinner, EmptyState, StatsCard } from '../components/Common';
import EntityLookup from '../components/Common/EntityLookup';
import { useSearch } from '../context/SearchContext';
import { useLanguage } from '../context/LanguageContext';
import { buildReportId } from '../utils/reportId';
import { printTable } from '../utils/printTable';

const selectStyles = {
  control: (base) => ({
    ...base,
    borderRadius: '0.375rem',
    borderColor: '#D1D5DB',
    minHeight: '42px',
  }),
};

const STATUSES = [
  'warten auf Gerät',
  'bereit zur Analyse',
  'warten auf KV',
  'warten auf Ersatzteil',
  'in Reparatur',
  'zu verrechnen',
  'abgeschlossen',
];

const statusColors = {
  test: 'bg-gray-100 text-gray-800',
  'warten auf Gerät': 'bg-gray-100 text-gray-800',
  'bereit zur Analyse': 'bg-blue-100 text-blue-800',
  'warten auf KV': 'bg-red-100 text-red-800',
  'warten auf Ersatzteil': 'bg-red-100 text-red-800',
  'in Reparatur': 'bg-blue-100 text-blue-800',
  'zu verrechnen': 'bg-yellow-100 text-yellow-800',
  abgeschlossen: 'bg-green-100 text-green-800',
};

const columnHeaderColors = {
  'warten auf Gerät': 'bg-slate-400 text-white',
  'bereit zur Analyse': 'bg-sky-400 text-white',
  'warten auf KV': 'bg-rose-400 text-white',
  'warten auf Ersatzteil': 'bg-orange-400 text-white',
  'in Reparatur': 'bg-blue-500 text-white',
  'zu verrechnen': 'bg-amber-400 text-white',
  abgeschlossen: 'bg-emerald-400 text-white',
};

const emptyForm = {
  type: 'Reparatur',
  dealer_id: '',
  customer_id: '',
  device_ids: [],
  problem_description: '',
  accessories: '',
};

const Repairs = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { searchTerm } = useSearch();
  const { t, language } = useLanguage();
  const dateLocale = language === 'de' ? 'de-DE' : 'en-GB';

  const { items: repairs, stats, loading, error } = useSelector((state) => state.repairs);
  const { items: customers } = useSelector((state) => state.customers);
  const { items: dealers } = useSelector((state) => state.dealers);
  const { items: devices } = useSelector((state) => state.devices);

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [viewMode, setViewMode] = useState('board');
  const [dragOverStatus, setDragOverStatus] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterCustomerId, setFilterCustomerId] = useState(null);
  const [filterDealerId, setFilterDealerId] = useState(null);
  const [filterDeviceId, setFilterDeviceId] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');

  // /repairs?new=1 (Dashboard quick action) opens the New Repair Ticket form
  // straight away; the param is dropped so a reload doesn't reopen it.
  useEffect(() => {
    if (new URLSearchParams(location.search).get('new')) {
      setFormData(emptyForm);
      setShowModal(true);
      navigate('/repairs', { replace: true });
    }
  }, [location.search, navigate]);

  // location.key changes on every navigation, including clicking the
  // Repairs nav link while already on this page, so that reloads too.
  useEffect(() => {
    dispatch(fetchRepairs());
    dispatch(fetchRepairStats());
    dispatch(fetchCustomers());
    dispatch(fetchDealers());
    dispatch(fetchDevices());
  }, [dispatch, location.key]);

  // Refresh tickets and counts when coming back to the tab, so changes made
  // elsewhere show up without a manual page reload.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        dispatch(fetchRepairs());
        dispatch(fetchRepairStats());
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [dispatch]);

  // Ctrl+P (Cmd+P on Mac) prints the filtered list as a plain table instead
  // of the browser printing the whole page. Goes through a ref because
  // handlePrint is defined further down (after the early error return).
  const printListRef = useRef(null);
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'p' && printListRef.current) {
        e.preventDefault();
        printListRef.current();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const customerOptions = customers.map((c) => ({ value: c.id, label: c.name }));
  const dealerOptions = dealers.map((d) => ({ value: d.id, label: d.name }));
  const deviceOptions = devices.map((d) => ({
    value: d.id,
    label: `${d.serial_number}${d.brand || d.model ? ` — ${[d.brand, d.model].filter(Boolean).join(' ')}` : ''}`,
  }));

  const hasActiveFilters =
    filterStatus ||
    filterType ||
    filterCustomerId ||
    filterDealerId ||
    filterDeviceId ||
    dateFrom ||
    dateTo ||
    priceMin ||
    priceMax;

  const clearFilters = () => {
    setFilterStatus('');
    setFilterType('');
    setFilterCustomerId(null);
    setFilterDealerId(null);
    setFilterDeviceId(null);
    setDateFrom('');
    setDateTo('');
    setPriceMin('');
    setPriceMax('');
  };

  const toLocalDateKey = (value) => {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const filtered = repairs.filter((r) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      buildReportId(r.id, r.created_at).toLowerCase().includes(term) ||
      r.customer_name?.toLowerCase().includes(term) ||
      r.dealer_name?.toLowerCase().includes(term) ||
      r.device_summary?.toLowerCase().includes(term) ||
      r.problem_description?.toLowerCase().includes(term) ||
      r.type?.toLowerCase().includes(term);
    const matchesStatus = !filterStatus || r.status === filterStatus;
    const matchesType = !filterType || r.type === filterType;
    const matchesCustomer = !filterCustomerId || r.customer_id === filterCustomerId;
    const matchesDealer = !filterDealerId || r.dealer_id === filterDealerId;
    const matchesDevice =
      !filterDeviceId ||
      (r.device_ids || '')
        .split(',')
        .map((id) => parseInt(id))
        .includes(filterDeviceId);
    // Local calendar date (created_at is UTC, so slicing the ISO string
    // would put tickets created late in the evening on the wrong day).
    const createdDate = r.created_at ? toLocalDateKey(r.created_at) : null;
    const matchesDateFrom = !dateFrom || (createdDate && createdDate >= dateFrom);
    const matchesDateTo = !dateTo || (createdDate && createdDate <= dateTo);
    const total = Number(r.total_cost) || 0;
    const matchesPriceMin = !priceMin || total >= parseFloat(priceMin);
    const matchesPriceMax = !priceMax || total <= parseFloat(priceMax);
    return (
      matchesSearch &&
      matchesStatus &&
      matchesType &&
      matchesCustomer &&
      matchesDealer &&
      matchesDevice &&
      matchesDateFrom &&
      matchesDateTo &&
      matchesPriceMin &&
      matchesPriceMax
    );
  });

  // A ticket can take several devices (customer brings in more than one).
  // Each picked device keeps its own picker (edit / swap / remove); the last
  // empty picker adds another one.
  const setDeviceAt = (index, id) =>
    setFormData((f) => {
      const ids = [...f.device_ids];
      if (!id) ids.splice(index, 1);
      else if (!ids.includes(id)) ids[index] = id;
      return { ...f, device_ids: ids };
    });

  const openCreate = () => {
    setFormData(emptyForm);
    setShowModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { device_ids, ...rest } = formData;
    const payload = {
      ...rest,
      dealer_id: formData.dealer_id || null,
      customer_id: formData.customer_id || null,
    };
    dispatch(createRepair(payload))
      .unwrap()
      .then(async (ticket) => {
        for (const device_id of device_ids) {
          try {
            await dispatch(addRepairDevice({ id: ticket.id, data: { device_id } })).unwrap();
          } catch (err) {
            console.error('Failed to attach device to new ticket:', err);
          }
        }
        setShowModal(false);
        navigate(`/repairs/${ticket.id}`);
      })
      .catch((err) => console.error('Failed to create repair ticket:', err));
  };

  const handleCardDrop = (status) => {
    setDragOverStatus(null);
    if (draggingId == null) return;
    const ticket = repairs.find((r) => r.id === draggingId);
    setDraggingId(null);
    if (!ticket || ticket.status === status) return;
    dispatch(updateRepairStatus({ id: draggingId, status }))
      .unwrap()
      .then((payload) => {
        if (payload.needsManualCompletion) {
          window.alert(t('repairs.invoiceDraftedAlert'));
        }
      })
      .catch((err) => alert(err?.error || t('repairs.updateStatusFailed')));
  };

  if (error) {
    return (
      <div className='card bg-red-50 border border-red-200'>
        <p className='text-red-600'>{t('repairs.errorLoading', { error })}</p>
      </div>
    );
  }

  const handlePrint = () => {
    printTable({
      title: t('repairs.title'),
      subtitle: [filterStatus, filterType].filter(Boolean).join(', '),
      columns: [
        { header: t('common.id') },
        { header: t('common.status') },
        { header: t('repairs.fieldType') },
        { header: t('repairs.colCustomerDealer') },
        { header: t('repairs.colDevice') },
        { header: t('common.total'), align: 'right' },
        { header: t('repairs.colCreated') },
      ],
      rows: filtered.map((r) => [
        buildReportId(r.id, r.created_at),
        r.status,
        r.type,
        r.customer_name || r.dealer_name,
        r.device_summary,
        `CHF ${Number(r.total_cost || 0).toFixed(2)}`,
        new Date(r.created_at).toLocaleDateString(dateLocale),
      ]),
    });
  };
  printListRef.current = handlePrint;

  return (
    <div>
      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6'>
        <div>
          <h1 className='text-3xl font-bold text-gray-900'>{t('repairs.title')}</h1>
          <p className='text-gray-600 mt-1'>{t('repairs.subtitle')}</p>
        </div>
        <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3'>
          <div className='flex rounded-lg border border-gray-300 overflow-hidden'>
            <button
              onClick={() => setViewMode('list')}
              className={`flex-1 sm:flex-none px-3 py-2 text-sm font-medium whitespace-nowrap ${viewMode === 'list' ? 'bg-[#361826] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              ☰ {t('repairs.viewList')}
            </button>
            <button
              onClick={() => setViewMode('board')}
              className={`flex-1 sm:flex-none px-3 py-2 text-sm font-medium whitespace-nowrap ${viewMode === 'board' ? 'bg-[#361826] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              🗂️ {t('repairs.viewBoard')}
            </button>
          </div>
          <button onClick={openCreate} className='btn-primary w-full sm:w-auto whitespace-nowrap'>
            {t('repairs.newRepairTicket')}
          </button>
        </div>
      </div>

      {stats && (
        <div className='grid grid-cols-2 md:grid-cols-4 gap-6 mb-6'>
          <StatsCard label={t('repairs.totalTickets')} value={stats.total_tickets || 0} icon='🩹' />
          <StatsCard label='In Reparatur' value={stats.in_reparatur || 0} color='blue' />
          <StatsCard label='Zu verrechnen' value={stats.zu_verrechnen || 0} color='orange' />
          <StatsCard label='Abgeschlossen' value={stats.abgeschlossen || 0} color='green' />
        </div>
      )}

      <div className='card mb-6 space-y-3'>
        <div className='flex flex-wrap items-center gap-3'>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className='input-field w-auto'
          >
            <option value=''>{t('repairs.allStatuses')}</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className='input-field w-auto'
          >
            <option value=''>{t('repairs.allTypes')}</option>
            <option value='Reparatur'>Reparatur</option>
            <option value='Kontrolle'>Kontrolle</option>
            <option value='Ersatzteile'>Ersatzteile</option>
          </select>
          <div className='flex items-center gap-2 text-sm text-gray-600'>
            <input
              type='date'
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className='input-field w-auto'
              title={t('repairs.createdFrom')}
            />
            <span>–</span>
            <input
              type='date'
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className='input-field w-auto'
              title={t('repairs.createdTo')}
            />
          </div>
          <div className='flex items-center gap-2 text-sm text-gray-600'>
            <input
              type='number'
              min='0'
              step='0.01'
              value={priceMin}
              onChange={(e) => setPriceMin(e.target.value)}
              className='input-field w-24'
              placeholder={t('repairs.priceMinPlaceholder')}
            />
            <span>–</span>
            <input
              type='number'
              min='0'
              step='0.01'
              value={priceMax}
              onChange={(e) => setPriceMax(e.target.value)}
              className='input-field w-24'
              placeholder={t('repairs.priceMaxPlaceholder')}
            />
          </div>
          {hasActiveFilters && (
            <button onClick={clearFilters} className='btn-secondary whitespace-nowrap'>
              {t('common.clearFilters')}
            </button>
          )}
        </div>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
          <Select
            classNamePrefix='react-select'
            options={customerOptions}
            value={customerOptions.find((opt) => opt.value === filterCustomerId) || null}
            onChange={(opt) => setFilterCustomerId(opt ? opt.value : null)}
            placeholder={t('repairs.filterByCustomer')}
            isClearable
            styles={selectStyles}
          />
          <Select
            classNamePrefix='react-select'
            options={dealerOptions}
            value={dealerOptions.find((opt) => opt.value === filterDealerId) || null}
            onChange={(opt) => setFilterDealerId(opt ? opt.value : null)}
            placeholder={t('repairs.filterByDealer')}
            isClearable
            styles={selectStyles}
          />
          <Select
            classNamePrefix='react-select'
            options={deviceOptions}
            value={deviceOptions.find((opt) => opt.value === filterDeviceId) || null}
            onChange={(opt) => setFilterDeviceId(opt ? opt.value : null)}
            placeholder={t('repairs.filterByDevice')}
            isClearable
            styles={selectStyles}
          />
        </div>
      </div>

      {loading && repairs.length === 0 ? (
        <LoadingSpinner />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon='🩹'
          title={t('repairs.emptyTitle')}
          message={t('repairs.emptyMessage')}
          actionLabel={t('repairs.newRepairTicket')}
          onAction={openCreate}
          hasItems={repairs.length > 0}
        />
      ) : viewMode === 'board' ? (
        <div className='flex gap-4 overflow-x-auto pb-4'>
          {STATUSES.map((status) => {
            const cards = filtered.filter((r) => r.status === status);
            return (
              <div
                key={status}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOverStatus(status);
                }}
                onDragLeave={() => setDragOverStatus((s) => (s === status ? null : s))}
                onDrop={(e) => {
                  e.preventDefault();
                  handleCardDrop(status);
                }}
                className={`flex-shrink-0 w-72 rounded-lg border-2 shadow-sm ${
                  dragOverStatus === status ? 'border-blue-400 bg-blue-50' : 'border-gray-100 bg-white'
                }`}
              >
                <div
                  className={`px-3 py-2 rounded-t-lg text-xs font-bold flex items-center justify-between ${columnHeaderColors[status] || 'bg-gray-400 text-white'}`}
                >
                  <span>{status}</span>
                  <span className='bg-white bg-opacity-30 rounded-full px-2'>{cards.length}</span>
                </div>
                <div className='p-2 space-y-2 max-h-[70vh] overflow-y-auto'>
                  {cards.length === 0 ? (
                    <p className='text-xs text-gray-400 text-center py-4'>{t('repairs.noTicketsInColumn')}</p>
                  ) : (
                    cards.map((r) => (
                      <div
                        key={r.id}
                        draggable
                        onDragStart={() => setDraggingId(r.id)}
                        onDragEnd={() => {
                          setDraggingId(null);
                          setDragOverStatus(null);
                        }}
                        onClick={() => navigate(`/repairs/${r.id}`)}
                        className='bg-white rounded-lg border border-gray-200 p-3 shadow-sm hover:shadow-md hover:border-blue-300 cursor-pointer transition-all'
                      >
                        <div className='flex items-center justify-between mb-1'>
                          <span className='text-sm font-semibold text-blue-600'>
                            {buildReportId(r.id, r.created_at)}
                          </span>
                          <span className='text-xs text-gray-400'>{r.type}</span>
                        </div>
                        <p className='text-sm text-gray-900 truncate'>{r.customer_name || r.dealer_name || '-'}</p>
                        <p className='text-xs text-gray-500 truncate'>{r.device_summary || '-'}</p>
                        <div className='flex items-center justify-between mt-2'>
                          <span className='text-xs text-gray-400'>
                            {new Date(r.created_at).toLocaleDateString(dateLocale)}
                          </span>
                          <span className='text-xs font-medium text-gray-700'>
                            CHF {Number(r.total_cost || 0).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className='card overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('common.id')}</th>
                <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('common.status')}</th>
                <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('repairs.fieldType')}</th>
                <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('repairs.colCustomerDealer')}</th>
                <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('repairs.colDevice')}</th>
                <th className='px-4 py-2 text-right text-xs font-medium text-gray-500'>{t('common.total')}</th>
                <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('repairs.colCreated')}</th>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200'>
              {filtered.map((r) => (
                <tr
                  key={r.id}
                  className='hover:bg-gray-50 cursor-pointer'
                  onClick={() => navigate(`/repairs/${r.id}`)}
                >
                  <td className='px-4 py-3 text-sm font-medium text-blue-600 hover:text-blue-800'>
                    {buildReportId(r.id, r.created_at)}
                  </td>
                  <td className='px-4 py-3'>
                    <span
                      className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${statusColors[r.status] || 'bg-gray-100 text-gray-800'}`}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className='px-4 py-3 text-sm text-gray-900'>{r.type}</td>
                  <td className='px-4 py-3 text-sm text-gray-900'>
                    {r.customer_name || r.dealer_name || '-'}
                  </td>
                  <td className='px-4 py-3 text-sm text-gray-900'>{r.device_summary || '-'}</td>
                  <td className='px-4 py-3 text-sm text-gray-900 text-right'>
                    CHF {Number(r.total_cost || 0).toFixed(2)}
                  </td>
                  <td className='px-4 py-3 text-sm text-gray-500'>
                    {new Date(r.created_at).toLocaleDateString(dateLocale)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={t('repairs.modalTitle')} size='md'>
        <form onSubmit={handleSubmit}>
          <div className='space-y-4'>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>{t('repairs.fieldType')}</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className='input-field'
              >
                <option value='Reparatur'>Reparatur</option>
                <option value='Kontrolle'>Kontrolle</option>
                <option value='Ersatzteile'>Ersatzteile</option>
              </select>
            </div>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <EntityLookup
                  kind='customer'
                  label={t('repairs.fieldCustomer')}
                  records={customers}
                  options={customerOptions}
                  value={formData.customer_id ? parseInt(formData.customer_id) : null}
                  onChange={(id) => setFormData((f) => ({ ...f, customer_id: id || '' }))}
                  placeholder={t('repairs.searchOrCreateCustomer')}
                />
              </div>
              <div>
                <EntityLookup
                  kind='dealer'
                  label={t('repairs.fieldDealer')}
                  records={dealers}
                  options={dealerOptions}
                  value={formData.dealer_id ? parseInt(formData.dealer_id) : null}
                  onChange={(id) => setFormData((f) => ({ ...f, dealer_id: id || '' }))}
                  placeholder={t('repairs.searchOrCreateDealer')}
                />
              </div>
            </div>
            <div>
              {formData.device_ids.map((deviceId, i) => (
                <div key={deviceId} className='mb-3'>
                  <EntityLookup
                    kind='device'
                    label={i === 0 ? t('repairs.fieldDevice') : ''}
                    records={devices}
                    options={deviceOptions.filter((o) => o.value === deviceId || !formData.device_ids.includes(o.value))}
                    value={deviceId}
                    onChange={(id) => setDeviceAt(i, id)}
                    placeholder={t('repairs.searchSerialOrCreateDevice')}
                    extra={
                      <button
                        type='button'
                        onClick={() => setDeviceAt(i, null)}
                        className='text-xs text-red-600 hover:text-red-800 font-normal'
                      >
                        {t('common.remove')}
                      </button>
                    }
                  />
                </div>
              ))}
              <EntityLookup
                kind='device'
                label={formData.device_ids.length === 0 ? t('repairs.fieldDevice') : ''}
                records={devices}
                options={deviceOptions.filter((o) => !formData.device_ids.includes(o.value))}
                value={null}
                onChange={(id) => id && setDeviceAt(formData.device_ids.length, id)}
                placeholder={
                  formData.device_ids.length
                    ? `+ ${t('repairs.searchSerialOrCreateDevice')}`
                    : t('repairs.searchSerialOrCreateDevice')
                }
              />
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>{t('repairs.fieldAccessories')}</label>
              <input
                type='text'
                value={formData.accessories}
                onChange={(e) => setFormData({ ...formData, accessories: e.target.value })}
                className='input-field'
                placeholder={t('repairs.accessoriesPlaceholder')}
              />
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>{t('repairs.fieldProblemDescription')}</label>
              <textarea
                value={formData.problem_description}
                onChange={(e) => setFormData({ ...formData, problem_description: e.target.value })}
                className='input-field'
                rows='3'
              />
            </div>
            <p className='text-xs text-gray-500'>
              {t('repairs.attachDeviceHint')}
            </p>
            <div className='flex justify-end space-x-3 pt-4'>
              <button type='button' onClick={() => setShowModal(false)} className='btn-secondary'>
                {t('common.cancel')}
              </button>
              <button type='submit' className='btn-primary'>
                {t('repairs.createTicket')}
              </button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Repairs;
